import React from 'react';
import {Filter, Loader, Search, X} from 'lucide-react';
import AppleSelect from '../components/Common/AppleSelect';
import CommonActiveFilters from '../components/Common/ActiveFilters';
import TabBar from '../components/Common/TabBar';
import Button from '../components/Common/Button';
import ActionMenu from '../components/Common/ActionMenu';
import DateInput from "../components/Common/DateInput";

// Shared building blocks for the Stock screen tabs. They reproduce the look of
// the catalogue list (src/components/CatalogPage/product/ProductList.js) so
// every tab of both screens reads the same.

// Page title bar + optional underlined tabs, as on the catalogue / stock pages.
// tabs: [{key, label, Icon}]
export const PageHeader = ({title, actions, tabs, activeTab, onTabChange}) => (
    <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="px-4 py-4 sm:px-8">
            <div className="page-header-row">
                <h1 className="page-title">{title}</h1>
                <div className="flex items-center gap-1 sm:gap-2">{actions}</div>
            </div>
        </div>
        {tabs && (
            <TabBar tabs={tabs} active={activeTab} onChange={onTabChange}
                    className="px-2 sm:px-4 border-b border-gray-200 dark:border-gray-700"/>
        )}
    </header>
);

// Page header button ("Importer", "Nouveau"…) — the shared Button, plain teal
// style for every header action; the label folds to the icon on mobile.
export const HeaderAction = ({icon, label, onClick, title, ...props}) => (
    <Button variant="plain" icon={icon} title={title || label} compact onClick={onClick} {...props}>
        {label}
    </Button>
);

// Edge-to-edge search field of the catalogue toolbar
export const SearchInput = ({value, onChange, placeholder}) => (
    <div className="relative flex-1">
        <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
        <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            aria-label={placeholder}
            className="w-full h-full min-h-[3.125rem] pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-primary transition-all placeholder:text-gray-400 shadow-sm"
        />
    </div>
);

// Toolbar select / date input, same height as SearchInput
export const toolbarFieldClass =
    'w-full py-3 px-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-2 focus:ring-primary focus:border-primary shadow-sm transition-all';

export const ToolbarSelect = ({value, onChange, label, children, className = 'sm:w-52'}) => (
    <div className={`flex min-w-0 ${className}`}>
        <AppleSelect variant="toolbar" icon={Filter} aria-label={label} value={value}
                     onChange={(e) => onChange(e.target.value)}>
            {children}
        </AppleSelect>
    </div>
);

export const FilterButton = ({count, onClick}) => (
    <button
        onClick={onClick}
        className="relative flex items-center justify-center sm:justify-start gap-2 px-6 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm hover:shadow transition-all"
    >
        <Filter className="w-5 h-5 text-gray-600 dark:text-gray-300"/>
        <span className="font-medium text-gray-700 dark:text-gray-300">Filtres</span>
        {count > 0 && (
            <span className="absolute -top-2 -right-2 bg-primary text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center shadow-lg">
                {count}
            </span>
        )}
    </button>
);

// « Filtres actifs » bar: the teal-themed Common/ActiveFilters (same as the
// catalogue). chips: [{key, label, style?, onRemove}] — a chip's
// style.backgroundColor (category colour) becomes its colour dot; the old
// per-chip colour classes are ignored so every chip matches the theme.
export const ActiveFilters = ({chips, onClearAll}) => (
    <CommonActiveFilters
        onClearAll={onClearAll}
        chips={chips.map(({key, label, style, icon, onRemove}) => ({key, label, icon, color: style?.backgroundColor, onRemove}))}
    />
);

// Flat counters strip under the toolbar; a cell is clickable when onSelect is given.
// items: [{key, label, value, color}]
// Literal class names so Tailwind's JIT picks them up
const STRIP_COLS = {2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-4', 5: 'sm:grid-cols-5', 6: 'sm:grid-cols-6'};

export const SummaryStrip = ({items, active, onSelect}) => (
    <div className={`grid grid-cols-2 ${STRIP_COLS[items.length] || 'sm:grid-cols-4'} border-b border-gray-200 dark:border-gray-700`}>
        {items.map(item => {
            const content = (<>
                <span className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{item.label}</span>
                <span className={`block text-xl font-bold ${item.color || 'text-gray-900 dark:text-gray-100'}`}>{item.value}</span>
                {item.hint && <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.hint}</span>}
            </>);
            return onSelect ? (
                <button key={item.key} onClick={() => onSelect(item.key)}
                        className={`px-4 sm:px-6 py-3 text-left border-b-2 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800 ${
                            active === item.key ? 'border-primary bg-primary/5' : 'border-transparent'
                        }`}>
                    {content}
                </button>
            ) : (
                <div key={item.key} className="px-4 sm:px-6 py-3">{content}</div>
            );
        })}
    </div>
);

export const ListLoader = ({label = 'Chargement...'}) => (
    <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <Loader className="w-10 h-10 text-primary animate-spin"/>
        <p className="text-gray-500 dark:text-gray-400">{label}</p>
    </div>
);

export const ListEmpty = ({icon: Icon, title, text, action}) => (
    <div className="flex flex-col items-center justify-center py-16 px-4">
        <Icon className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4"/>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{title}</h3>
        {text && <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-md">{text}</p>}
        {action}
    </div>
);

// Date input sized like the other toolbar fields
export const ToolbarDate = ({value, onChange, label, min, max, className = 'sm:w-44'}) => (
    <div className={`relative ${className}`}>
        <DateInput aria-label={label} title={label} value={value} min={min} max={max} clearable={false}
                   onChange={(e) => e.target.value && onChange(e.target.value)}
                   className={toolbarFieldClass}/>
    </div>
);

// Square white container holding the table + mobile cards
export const ListCard = ({children}) => (
    <div className="bg-white dark:bg-gray-800 shadow-sm overflow-hidden border border-gray-200 dark:border-gray-700">
        {children}
    </div>
);

// ListCard with an uppercase title bar; `aside` sits on the right of the title
export const Panel = ({title, aside, children}) => (
    <ListCard>
        <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">{title}</h2>
            {aside}
        </div>
        {children}
    </ListCard>
);

export const thClass = 'px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider';
export const mobileCardClass = 'bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600 hover:shadow-md transition-all';
// Mobile list like the catalogue (Menus): edge-to-edge rows split by a
// hairline, no outer margin and no gap between rows.
export const mobileListClass = 'lg:hidden divide-y divide-gray-200 dark:divide-gray-700';
export const mobileRowClass = 'p-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors';

// Coloured thumbnail with two initials, like catalogue products
export const Avatar = ({name = '', color, size = 'md'}) => (
    <div
        className={`${size === 'lg' ? 'h-16 w-16 text-base' : size === 'sm' ? 'h-9 w-9 text-xs' : 'h-12 w-12 text-sm'} flex-shrink-0 flex items-center justify-center text-white font-bold rounded-lg shadow-sm`}
        style={{backgroundColor: color && color.toLowerCase() !== '#ffffff' ? color : '#4DB6AC'}}
    >
        {name.substring(0, 2).toUpperCase()}
    </div>
);

// Row "⋯" menu — the shared ActionMenu. actions: [{label, icon, onClick, danger?, disabled?, hidden?} | {divider: true}]
// (`tone` is accepted for older call sites and ignored: the menu highlights in teal, red when danger)
export const RowActions = ({actions, label}) => (
    <ActionMenu label={label} items={actions.map(({tone, ...a}) => a)}/>
);

// "Afficher [n] par page" + Précédent / numbers / Suivant
export const Pagination = ({page, totalPages, onPage, itemsPerPage, onItemsPerPage, options = [10, 25, 50, 100], summary}) => {
    const pages = Array.from({length: totalPages}, (_, i) => i + 1);
    const navClass = (disabled) => `px-4 py-2 rounded-lg font-medium transition-all ${
        disabled
            ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm'
    }`;
    return (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 py-4 px-4">
            <div className="flex items-center gap-2">
                {onItemsPerPage && (<>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Afficher:</span>
                    <AppleSelect
                        value={itemsPerPage}
                        onChange={(e) => onItemsPerPage(Number(e.target.value))}
                        className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all cursor-pointer"
                    >
                        {options.map(v => <option key={v} value={v}>{v}</option>)}
                    </AppleSelect>
                    <span className="text-sm text-gray-600 dark:text-gray-400">par page</span>
                </>)}
                {summary && <span className="text-sm text-gray-600 dark:text-gray-400">{onItemsPerPage ? '· ' : ''}{summary}</span>}
            </div>
            {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2">
                    <button onClick={() => onPage(Math.max(page - 1, 1))} disabled={page === 1} className={navClass(page === 1)}>
                        Précédent
                    </button>
                    <div className="flex gap-1">
                        {pages.slice(Math.max(0, page - 3), Math.min(totalPages, page + 2)).map(p => (
                            <button key={p} onClick={() => onPage(p)}
                                    className={`w-10 h-10 rounded-lg font-medium transition-all ${
                                        p === page
                                            ? 'bg-primary text-white shadow-md'
                                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                    }`}>
                                {p}
                            </button>
                        ))}
                    </div>
                    <button onClick={() => onPage(Math.min(page + 1, totalPages))} disabled={page === totalPages} className={navClass(page === totalPages)}>
                        Suivant
                    </button>
                </div>
            )}
        </div>
    );
};

// Catalogue popup: centred title, close cross, scrollable body, optional footer.
// Slides up as a sheet on mobile.
const MODAL_SIZES = {sm: 'sm:max-w-[500px]', md: 'sm:max-w-lg', lg: 'sm:max-w-2xl', xl: 'sm:max-w-4xl', '2xl': 'sm:max-w-6xl'};

// bodyClass: '' gives an edge-to-edge body (strips, hairline lists).
export const Modal = ({title, subtitle, onClose, children, footer, size = 'md', bodyClass = 'px-6 py-4'}) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
        <div className={`bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-lg shadow-lg w-full ${MODAL_SIZES[size]} sm:mx-4 overflow-hidden flex flex-col max-h-[90vh]`}>
            <div className="relative px-12 py-5 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 text-center">
                <h3 className="text-lg font-medium text-gray-800 dark:text-white">{title}</h3>
                {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">{subtitle}</p>}
                <button type="button" onClick={onClose} aria-label="Fermer"
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                    <X className="w-4 h-4"/>
                </button>
            </div>
            <div className={`${bodyClass} overflow-y-auto flex-1`}>{children}</div>
            {footer && <div className="flex-shrink-0">{footer}</div>}
        </div>
    </div>
);

// "Annuler | Action" footer of the catalogue popups; tone colours the action text.
// With `form`, the action submits that form instead of calling onConfirm.
export const ModalFooter = ({onCancel, onConfirm, label, saving, disabled, tone = 'text-primary', form, cancelLabel = 'Annuler'}) => (
    <div className="grid grid-cols-2 border-t border-gray-200 dark:border-gray-700">
        <button type="button" onClick={onCancel} disabled={saving}
                className="py-4 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium disabled:opacity-50">
            {cancelLabel}
        </button>
        <button type={form ? 'submit' : 'button'} form={form} onClick={form ? undefined : onConfirm} disabled={saving || disabled}
                className={`py-4 font-medium border-l border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 flex items-center justify-center gap-2 ${tone}`}>
            {saving && <Loader className="w-4 h-4 animate-spin"/>}
            {label}
        </button>
    </div>
);
